import type {
  HostTutorResponse,
  TutorLessonOption,
  TutorSearchResult,
  TutorSessionType,
  TutorWeeklyWorkingHours,
  UpdateUserTutorProfileRequest,
  UserTutorProfileSettings,
} from '../models/Tutor';
import type { BookingSlotsConfig } from '../models/Room';
import {
  normalizeTutorTimezone,
  normalizeTutorWorkingHours,
  serializeTutorWorkingHours,
  type TutorWorkingHoursDraft,
} from './tutorWorkingHours';
import { tutorSpaceAddressWriteFromDraft, type PlaceAddressDraft } from './placeAddress';

export type { TutorSessionType } from '../models/Tutor';

/** Canonical lesson place values — matches API `TutorSessionType` after three-way cutover. */
export const TUTOR_SESSION_TYPES: TutorSessionType[] = ['ONLINE', 'AT_TEACHER', 'AT_STUDENT'];

/** Legacy API value; normalize to `AT_TEACHER` on read. */
export const TUTOR_SESSION_TYPE_OFFLINE_LEGACY = 'OFFLINE';

export function isTutorSessionType(value: unknown): value is TutorSessionType {
  return value === 'ONLINE' || value === 'AT_TEACHER' || value === 'AT_STUDENT';
}

/**
 * Maps legacy `OFFLINE` → `AT_TEACHER`; keeps known three-way values.
 * Returns null for unrecognized input.
 */
export function normalizeTutorSessionType(value: unknown): TutorSessionType | null {
  if (typeof value !== 'string') return null;
  const raw = value.trim().toUpperCase();
  if (raw === TUTOR_SESSION_TYPE_OFFLINE_LEGACY || raw === 'AT_TEACHER') return 'AT_TEACHER';
  if (raw === 'ONLINE') return 'ONLINE';
  if (raw === 'AT_STUDENT') return 'AT_STUDENT';
  return null;
}

export function normalizeTutorSessionTypes(values: unknown): TutorSessionType[] {
  if (!Array.isArray(values)) return [];
  const normalized: TutorSessionType[] = [];
  for (const value of values) {
    const next = normalizeTutorSessionType(value);
    if (next && !normalized.includes(next)) {
      normalized.push(next);
    }
  }
  return normalized;
}

export function isAtTeacherSession(sessionType: unknown): boolean {
  return normalizeTutorSessionType(sessionType) === 'AT_TEACHER';
}

export function isAtStudentSession(sessionType: unknown): boolean {
  return normalizeTutorSessionType(sessionType) === 'AT_STUDENT';
}

export function isOnlineSession(sessionType: unknown): boolean {
  return normalizeTutorSessionType(sessionType) === 'ONLINE';
}

/** Listing needs a lesson-location space/address when teacher-place is offered. */
export function supportsAtTeacherPlace(types: TutorSessionType[] | null | undefined): boolean {
  return (types ?? []).some((type) => isAtTeacherSession(type));
}

/** Listing needs student-place cities when student-place lessons are offered. */
export function supportsAtStudentPlace(types: TutorSessionType[] | null | undefined): boolean {
  return (types ?? []).some((type) => isAtStudentSession(type));
}

/** @deprecated Prefer {@link supportsAtTeacherPlace}. */
export function supportsOfflineSession(types: TutorSessionType[] | null | undefined): boolean {
  return supportsAtTeacherPlace(types);
}

/**
 * Trims city names and removes case-insensitive duplicates (keeps first casing).
 * Matches backend normalization for `studentLessonCities`.
 */
export function normalizeStudentLessonCities(values: unknown): string[] {
  if (!Array.isArray(values)) return [];
  const normalized: string[] = [];
  const seen = new Set<string>();
  for (const value of values) {
    if (typeof value !== 'string') continue;
    const trimmed = value.trim();
    if (!trimmed) continue;
    const key = trimmed.toLocaleLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    normalized.push(trimmed);
  }
  return normalized;
}

/** Host walk-in / calendar: room required only at teacher's place. */
export function tutorSessionNeedsRoom(sessionType: unknown): boolean {
  return isAtTeacherSession(sessionType);
}

export function getTutorSessionTypeLabel(
  sessionType: TutorSessionType | null | undefined,
  translate: (key: string, fallback: string) => string
): string {
  const normalized = normalizeTutorSessionType(sessionType);
  if (normalized === 'ONLINE') {
    return translate('tutor.sessionType.online', 'Online');
  }
  if (normalized === 'AT_TEACHER') {
    return translate('tutor.sessionType.atTeacher', "At teacher's place");
  }
  if (normalized === 'AT_STUDENT') {
    return translate('tutor.sessionType.atStudent', "At student's place");
  }
  return '—';
}

export function buildUpdateTutorProfilePayload(input: {
  spaceId?: string | null;
  headline: string | null;
  overview: string | null;
  skills: UpdateUserTutorProfileRequest['skills'];
  languages: string[];
  profileImageUrl: string | null;
  /** Omit to leave gallery unchanged; pass `[]` to clear or a list to replace. */
  imageUrls?: string[] | null;
  supportedSessionTypes: TutorSessionType[];
  /** Ignored unless `AT_STUDENT` is supported; cleared to `[]` when it is not. */
  studentLessonCities?: string[] | null;
  workingHoursDraft?: TutorWorkingHoursDraft | null;
  timezone?: string | null;
  placeAddressDraft?: PlaceAddressDraft | null;
  bookingSlotsConfig?: BookingSlotsConfig | null;
  visible?: boolean | null;
}): UpdateUserTutorProfileRequest {
  const supportedSessionTypes = normalizeTutorSessionTypes(input.supportedSessionTypes);
  const workingHours: TutorWeeklyWorkingHours | null = input.workingHoursDraft
    ? serializeTutorWorkingHours(input.workingHoursDraft)
    : null;
  const timezone = normalizeTutorTimezone(input.timezone);
  const studentLessonCities = supportsAtStudentPlace(supportedSessionTypes)
    ? normalizeStudentLessonCities(input.studentLessonCities)
    : [];

  const base: UpdateUserTutorProfileRequest = {
    spaceId: input.spaceId ?? null,
    headline: input.headline,
    overview: input.overview,
    skills: input.skills,
    languages: input.languages,
    profileImageUrl: input.profileImageUrl,
    supportedSessionTypes,
    studentLessonCities,
    workingHours,
    timezone,
    ...(input.imageUrls !== undefined ? { imageUrls: input.imageUrls } : {}),
    ...(input.bookingSlotsConfig !== undefined
      ? { bookingSlotsConfig: input.bookingSlotsConfig }
      : {}),
    ...(input.visible !== undefined ? { visible: input.visible } : {}),
  };

  if (supportsAtTeacherPlace(supportedSessionTypes) && input.placeAddressDraft) {
    return {
      ...base,
      ...tutorSpaceAddressWriteFromDraft(input.placeAddressDraft),
    };
  }

  return base;
}

export function mapLessonOptionFromApi(
  option: Omit<TutorLessonOption, 'sessionType'> & { sessionType?: unknown }
): TutorLessonOption {
  return {
    ...option,
    sessionType: normalizeTutorSessionType(option.sessionType) ?? 'AT_TEACHER',
  };
}

export function mapTutorSearchResultFromApi(
  tutor: TutorSearchResult & {
    heading?: string | null;
    tutorHeadline?: string | null;
    supportedSessionTypes?: unknown;
    studentLessonCities?: unknown;
    lessonOptions?: Array<Omit<TutorLessonOption, 'sessionType'> & { sessionType?: unknown }>;
  }
): TutorSearchResult {
  const { heading, tutorHeadline, supportedSessionTypes, studentLessonCities, lessonOptions, ...rest } =
    tutor;
  return {
    ...rest,
    headline: (rest.headline ?? heading ?? tutorHeadline) ?? null,
    supportedSessionTypes: normalizeTutorSessionTypes(supportedSessionTypes),
    studentLessonCities: normalizeStudentLessonCities(studentLessonCities),
    workingHours: normalizeTutorWorkingHours(tutor.workingHours),
    timezone: normalizeTutorTimezone(tutor.timezone),
    lessonOptions: (lessonOptions ?? []).map(mapLessonOptionFromApi),
  };
}

export function mapTutorProfileSettingsFromApi(
  settings: UserTutorProfileSettings & {
    lessonOptions?: Array<Omit<TutorLessonOption, 'sessionType'> & { sessionType?: unknown }>;
    supportedSessionTypes?: unknown;
    studentLessonCities?: unknown;
  }
): UserTutorProfileSettings {
  return {
    ...settings,
    supportedSessionTypes: normalizeTutorSessionTypes(settings.supportedSessionTypes),
    studentLessonCities: normalizeStudentLessonCities(settings.studentLessonCities),
    workingHours: normalizeTutorWorkingHours(settings.workingHours),
    timezone: normalizeTutorTimezone(settings.timezone),
    lessonOptions: (settings.lessonOptions ?? []).map(mapLessonOptionFromApi),
  };
}

export function mapHostTutorResponseFromApi(
  tutor: HostTutorResponse & {
    lessonOptions?: Array<Omit<TutorLessonOption, 'sessionType'> & { sessionType?: unknown }>;
    supportedSessionTypes?: unknown;
    studentLessonCities?: unknown;
  },
): HostTutorResponse {
  return {
    ...tutor,
    supportedSessionTypes: normalizeTutorSessionTypes(tutor.supportedSessionTypes),
    studentLessonCities: normalizeStudentLessonCities(tutor.studentLessonCities),
    workingHours: normalizeTutorWorkingHours(tutor.workingHours),
    timezone: normalizeTutorTimezone(tutor.timezone),
    lessonOptions: (tutor.lessonOptions ?? []).map(mapLessonOptionFromApi),
  };
}

export function extractTutorProfileValidationMessage(error: unknown): string | null {
  if (typeof error !== 'object' || error == null || !('response' in error)) {
    return null;
  }
  const response = (error as { response?: { data?: { message?: unknown } } }).response;
  const message = response?.data?.message;
  return typeof message === 'string' && message.trim() ? message.trim() : null;
}
